import {Injectable, OnDestroy} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from '../../store';
import {DbStoreState} from "../../store";
import {PluginConnectService} from "../services/plugin-connect/plugin-connect.service";
import {UserSubscription} from "../../user/user-subscription";
import {combineLatest} from "rxjs";
import {DialogService} from "../../../ng2-bootstrap-modal/dialog.service";
import {CreateWebsiteService} from "../../services/local/create-website.service";
import {Config} from "../../config/config";
import {Website} from "../../websites/website/website";
import {Subject} from "rxjs";

/** check url and connect website
 * !important must be declared after booster guard!
 **/

@Injectable({
  providedIn: 'root'
})
export class PluginConnectGuard implements CanActivate, OnDestroy {

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  constructor(
    private readonly _router: Router,
    private readonly _store: Store<DbStoreState>,
    private readonly _pluginConnectService: PluginConnectService,
    private readonly _dialogService: DialogService,
    private readonly _createWebsiteService: CreateWebsiteService,
  ) {}

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {

    return new Promise<boolean>((resolve) => {
      if (!this._pluginConnectService.pluginData || !this._pluginConnectService.pluginData.returnUrl) {
        return resolve(true);
      }

      this._store.select(fromStore.getUserLoaded)
        .pipe(
          first(loaded => loaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserSubscription)
          )),
          first()
        )
        .subscribe(([user, userSubscription]) => {
          resolve(true);
          if (!user) {
            return;
          }
          if (user && !user.websiteConnected && !user.websiteConnectedSpeed && userSubscription && Config.photoGalleryPluginIds.includes(userSubscription.product_id)) {
            return;
          }

          this._store
            .select(fromStore.getWebsitesLoaded)
            .pipe(
              takeUntil(this._destroyStream$),
              filter((loaded: boolean): boolean => !!loaded),
              switchMap(() => this._store.select(fromStore.getWebsites)),
              first(websites => !!websites)
            )
            .subscribe((websites: Website[]) => {
              if (this._checkLimitExceeded(userSubscription, this._pluginConnectService.pluginData && this.checkSameWebsite(this._pluginConnectService.pluginData.siteUrl, websites), websites)) {
                this._createWebsiteService.openLimitExceededModal();
                this._pluginConnectService.resetPluginData();
                return;
              }
              this._pluginConnectService.connect(user.email, user.workspaces);
            });
        });
    });
  }

  private _checkLimitExceeded(userSubscription: UserSubscription, sameWebsiteConnect: boolean, websites: Website[]): boolean {
    if (!userSubscription) return false;
    const speedConnectedWebsites = websites.filter((website) => website.connectedFrom === 'speed_optimizer');
    const speedOptimizerDomainCount = userSubscription && userSubscription.plan && userSubscription.plan.services && userSubscription.plan.services.speed_optimizer && userSubscription.plan.services.speed_optimizer.speed_optimizer_domain_count.parameter_value;
    if (!userSubscription.domainsPermission.canConnectSpeedWebsite && this._pluginConnectService.pluginData.pluginName === '10webspeedoptimizer' && sameWebsiteConnect && speedConnectedWebsites.length <= +speedOptimizerDomainCount) {
      return false;
    }
    if (this._pluginConnectService.pluginData.pluginName === '10webplugin') {
      return !userSubscription.domainsPermission.canConnectWebsite;
    }
    else if (this._pluginConnectService.pluginData.pluginName === 'ai_assistant') {
      return !userSubscription.domainsPermission.canConnectAIWebsite;
    }
    else {
      return !userSubscription.domainsPermission.canConnectSpeedWebsite;
    }
  }

  private checkSameWebsite(siteUrl, websites) {
    return !!websites.find(website => website.site_url === siteUrl && !website.domain_hash);
  }



}
